import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, CardHeader, Col, Input, Row, Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import usePropertyEstimate from '../../hooks/usePropertyEstimate';
import {
  EstimateClassification,
  EstimateMarket,
  EstimateMissing,
} from '../../models/estimateModel';
import EstimateRangeBar from './EstimateRangeBar';

interface Props {
  propertyId: string;
  market: EstimateMarket;
}

// Mismos colores y textos que el bloque "Propi Estimate" del sitio público (web Next).
const DARK_GREEN = '#092417';
const TONE: Record<EstimateClassification, { bg: string; text: string; tag: string }> = {
  low: { bg: 'rgba(47,182,114,0.3)', text: '#2FB672', tag: 'Pocas así en la zona' },
  average: { bg: 'rgba(59,130,246,0.3)', text: '#93C5FD', tag: 'En el promedio' },
  high: { bg: 'rgba(250,113,45,0.3)', text: '#FA712D', tag: 'Sobre el promedio' },
};

// Qué falta para poder calcular.
const MISSING_LABEL: Record<EstimateMissing, string> = {
  price: 'el precio',
  area: 'el área (m²)',
  zone: 'la zona',
};

const MAX_ANALYSIS_LENGTH = 1500;

// Estimate de precio de la propiedad (renta o venta), presentado como lo ve el público. Se
// calcula solo al guardar precio, área, zona o tipo; aquí se puede recalcular y editar el
// análisis que se muestra en el sitio.
const PropertyEstimateCard: React.FC<Props> = ({ propertyId, market }) => {
  const { estimate, loaded, isLoading, recompute, saveAnalysis, generateAnalysis } =
    usePropertyEstimate({
      propertyId,
      market,
    });
  const [draft, setDraft] = useState('');

  useEffect(() => {
    setDraft(estimate?.analysis?.text ?? '');
  }, [estimate?.analysis?.text]);

  const marketLabel = market === 'rent' ? 'renta' : 'venta';
  const indicators = estimate?.indicators;
  const symbol = indicators?.config?.symbol ?? '$';
  const money = (value?: number | null) =>
    value || value === 0
      ? `${symbol}${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: value < 100 ? 2 : 0,
          maximumFractionDigits: value < 100 ? 2 : 0,
        })}`
      : '--';
  const analysisChanged = draft.trim() !== (estimate?.analysis?.text ?? '').trim();

  // Rangos: total (precio) y por m². La banda "promedio" del precio total se deriva
  // proporcionalmente de la del m², igual que en el sitio público.
  const classification: EstimateClassification =
    indicators?.valuation?.classification ?? 'average';
  const tone = TONE[classification];
  const priceLabel = (indicators?.labels ?? []).find((label) => label.type === 'price');
  const polygon = indicators?.polygon;
  const priceMin = Number(polygon?.price_min) || 0;
  const priceMax = Number(polygon?.price_max) || 0;
  const rangeMin = parseFloat(polygon?.range?.active_min ?? '0');
  const rangeMax = parseFloat(polygon?.range?.active_max ?? '0');
  const rangeMinAvg = parseFloat(polygon?.range?.confidence_lower ?? '0');
  const rangeMaxAvg = parseFloat(polygon?.range?.confidence_upper ?? '0');
  const ratioMin = rangeMax > rangeMin ? (rangeMinAvg - rangeMin) / (rangeMax - rangeMin) : 0.25;
  const ratioMax = rangeMax > rangeMin ? (rangeMaxAvg - rangeMin) / (rangeMax - rangeMin) : 0.75;
  const priceMinAvg = priceMin + ratioMin * (priceMax - priceMin);
  const priceMaxAvg = priceMin + ratioMax * (priceMax - priceMin);
  const price = estimate?.inputs?.price ?? 0;
  const pricePerM2 = indicators?.input?.price_m2 ?? 0;

  return (
    <Card className="mt-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h3 className="mb-0">Propi Estimate ({marketLabel})</h3>
          <small className="text-muted">
            Se calcula solo al guardar precio, área, zona o tipo de propiedad.
          </small>
        </div>
        <Button
          color="secondary"
          outline
          size="sm"
          type="button"
          disabled={isLoading || estimate?.status === 'not_applicable'}
          onClick={recompute}
        >
          {isLoading && <Spinner size="sm" className="mr-2" />}
          Recalcular
        </Button>
      </CardHeader>

      <CardBody>
        {!loaded && <Spinner size="sm" />}

        {loaded && !estimate && (
          <Alert color="secondary" className="mb-0">
            Aún no hay estimate de {marketLabel}. Se calcula al guardar el precio (también necesita
            el área y la zona). Si ya los tiene, pulsa "Recalcular".
          </Alert>
        )}

        {estimate?.status === 'not_applicable' && (
          <Alert color="secondary" className="mb-0">
            Los terrenos no llevan estimate.
          </Alert>
        )}

        {estimate?.status === 'incomplete' && (
          <Alert color="warning" className="mb-0">
            Falta {(estimate.missing ?? []).map((item) => MISSING_LABEL[item]).join(', ')} para
            calcular el estimate.{' '}
            {(estimate.missing ?? []).includes('area') && (
              <Link to={`/properties/admin/${propertyId}/amenities`}>Cargar el área</Link>
            )}{' '}
            {(estimate.missing ?? []).includes('zone') && (
              <Link to={`/properties/admin/${propertyId}/address-location`}>Asignar la zona</Link>
            )}
          </Alert>
        )}

        {estimate?.status === 'error' && (
          <Alert color="danger" className="mb-0">
            No se pudo calcular: {estimate.error ?? 'el servicio de direcciones no respondió'}.
            Intenta con "Recalcular".
          </Alert>
        )}

        {estimate?.status === 'ready' && (
          <>
            {/* Así lo ve el público */}
            <div style={{ background: DARK_GREEN, borderRadius: 12, color: '#fff', padding: 16 }}>
              {estimate.scope === 'city' && (
                <small className="d-block mb-2" style={{ color: '#fcd34d' }}>
                  Comparado con toda la ciudad: no hay datos de mercado de este barrio.
                </small>
              )}
              <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
                <span style={{ fontSize: 15 }}>
                  Propiedades similares: {money(priceMin)} – {money(priceMax)}
                </span>
                {priceLabel && (
                  <span
                    style={{
                      background: tone.bg,
                      borderRadius: 8,
                      padding: '6px 12px',
                      fontSize: 14,
                    }}
                  >
                    {priceLabel.title}
                    {priceLabel.subtitle ? ` — ${priceLabel.subtitle}` : ''}
                  </span>
                )}
              </div>

              {priceMin > 0 && priceMax > 0 && (
                <EstimateRangeBar
                  value={price}
                  min={priceMin}
                  minAverage={priceMinAvg}
                  maxAverage={priceMaxAvg}
                  max={priceMax}
                />
              )}
              <small className="d-block mt-2" style={{ color: '#9ca3af' }}>
                Precio publicado: <strong style={{ color: '#fff' }}>{money(price)}</strong> · Rango
                promedio: {money(priceMinAvg)} – {money(priceMaxAvg)}
              </small>

              <hr style={{ borderColor: 'rgba(255,255,255,0.15)' }} />

              <div className="d-flex align-items-start justify-content-between mb-1">
                <strong style={{ fontSize: 15 }}>Precio por m²</strong>
                <span
                  style={{
                    background: tone.bg,
                    borderRadius: 999,
                    padding: '2px 10px',
                    fontSize: 12,
                  }}
                >
                  {tone.tag}
                </span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 600, color: tone.text, lineHeight: 1.2 }}>
                {money(pricePerM2)}{' '}
                <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 400 }}>/ m²</span>
              </div>
              <div className="mb-3" style={{ fontSize: 13, color: '#9ca3af' }}>
                Lo habitual en esta zona es entre {money(rangeMinAvg)} y {money(rangeMaxAvg)} el m²
              </div>
              {rangeMin > 0 && rangeMax > 0 && (
                <EstimateRangeBar
                  value={pricePerM2}
                  min={rangeMin}
                  minAverage={rangeMinAvg}
                  maxAverage={rangeMaxAvg}
                  max={rangeMax}
                />
              )}
            </div>

            {/* Datos de apoyo para el admin */}
            <Row className="mt-3">
              <Col md="3" className="mb-2">
                <small className="text-muted d-block">Oferta sugerida</small>
                <strong>{money(estimate.suggested_offer)}</strong>
                <small className="text-muted d-block">
                  {estimate.suggested_offer_basis === 'min_price'
                    ? `Precio mínimo (${money(estimate.inputs?.min_price)}) + 5 %`
                    : 'Según el mercado (sin precio mínimo configurado)'}
                </small>
              </Col>
              <Col md="3" className="mb-2">
                <small className="text-muted d-block">Precio esperado de la zona</small>
                <strong>{money(indicators?.valuation?.price_expected)}</strong>
              </Col>
              <Col md="3" className="mb-2">
                <small className="text-muted d-block">Área</small>
                <strong>{estimate.inputs?.area_m2} m²</strong>
              </Col>
              <Col md="3" className="mb-2">
                <small className="text-muted d-block">Promedio de la zona por m²</small>
                <strong>{money(Number(polygon?.price_m2) || null)}</strong>
              </Col>
              {polygon?.avg_close_days ? (
                <Col md="3" className="mb-2">
                  <small className="text-muted d-block">Días promedio en cerrar</small>
                  <strong>{polygon.avg_close_days} días</strong>
                </Col>
              ) : null}
              <Col md="9" className="mb-2">
                <small className="text-muted d-block">Calculado</small>
                <strong>{new Date(estimate.computed_at).toLocaleString('es')}</strong>
              </Col>
            </Row>

            <h5 className="text-uppercase text-muted ls-1 mb-2 mt-3">
              Análisis (lo que ve el público)
            </h5>
            {estimate.analysis?.stale && (
              <Alert color="warning">
                Cambió el precio, el área o la zona después de escribir este análisis. Revísalo y
                guárdalo de nuevo.
              </Alert>
            )}
            <Input
              type="textarea"
              rows={4}
              maxLength={MAX_ANALYSIS_LENGTH}
              placeholder="Aún no hay análisis. Se genera con IA al calcular el estimate (tarda unos segundos) o escríbelo aquí; si no hay texto, el sitio no muestra el bloque de análisis."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="d-flex align-items-center justify-content-between mt-2">
              <small className="text-muted">
                {estimate.analysis
                  ? `Fuente: ${estimate.analysis.source === 'ai' ? 'IA' : 'escrito a mano'}`
                  : 'Sin análisis'}
                {' · '}
                {draft.length}/{MAX_ANALYSIS_LENGTH}
              </small>
              <div>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  type="button"
                  className="mr-2"
                  disabled={isLoading}
                  onClick={() => {
                    if (
                      estimate.analysis?.source === 'manual' &&
                      !window.confirm(
                        'Hay un análisis escrito a mano. Regenerar con IA lo reemplaza. ¿Continuar?',
                      )
                    ) {
                      return;
                    }
                    generateAnalysis();
                  }}
                >
                  {isLoading && <Spinner size="sm" className="mr-1" />}
                  Regenerar con IA
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  type="button"
                  disabled={isLoading || !draft.trim() || !analysisChanged}
                  onClick={() => saveAnalysis(draft)}
                >
                  Guardar análisis
                </Button>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PropertyEstimateCard;
